import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useMobileContext } from '../components/Layout';
import { MobileHeader } from '../components/mobile';
import { 
  Shield, Download, UserX, FileCheck, Clock, AlertTriangle,
  Archive, RefreshCw, Trash2, Eye, Search, Filter, X,
  CheckCircle, XCircle, FileText, Users, Database, Lock,
  Calendar, Building, ChevronDown, ChevronUp, History
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RGPDArchivage = () => {
  const { user, token } = useAuth();
  const { isMobile, onMenuClick } = useMobileContext();
  
  const [activeTab, setActiveTab] = useState('rgpd'); // 'rgpd', 'archives', 'consents', 'registry'
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // RGPD State
  const [rgpdStats, setRgpdStats] = useState(null);
  const [rgpdRequests, setRgpdRequests] = useState([]);
  const [retentionPeriods, setRetentionPeriods] = useState({});
  const [treatments, setTreatments] = useState([]);
  const [consentTypes, setConsentTypes] = useState([]);
  
  // Archives State
  const [archives, setArchives] = useState([]);
  const [archiveStats, setArchiveStats] = useState(null);
  const [pendingArchives, setPendingArchives] = useState([]);
  const [archiveTypes, setArchiveTypes] = useState({});
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  
  // Modals
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAnonymizeModal, setShowAnonymizeModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [exportSummary, setExportSummary] = useState(null);

  const fetchRGPDData = useCallback(async () => {
    try {
      const [statsRes, requestsRes, retentionRes, treatmentsRes, consentTypesRes] = await Promise.all([
        axios.get(`${API}/rgpd/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/rgpd/requests`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/rgpd/retention-periods`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/rgpd/treatments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/rgpd/consent-types`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setRgpdStats(statsRes.data);
      setRgpdRequests(requestsRes.data.requests || []);
      setRetentionPeriods(retentionRes.data.retention_periods || {});
      setTreatments(treatmentsRes.data.treatments || []);
      setConsentTypes(consentTypesRes.data.consent_types || []);
    } catch (error) {
      console.error('Error fetching RGPD data:', error);
    }
  }, [token]);

  const fetchArchiveData = useCallback(async () => {
    try {
      const [archivesRes, statsRes, pendingRes, typesRes] = await Promise.all([
        axios.get(`${API}/archives?limit=50`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/archives/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/archives/pending`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/archives/types`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setArchives(archivesRes.data.archives || []);
      setArchiveStats(statsRes.data);
      setPendingArchives(pendingRes.data.pending || []);
      setArchiveTypes(typesRes.data.types || {});
    } catch (error) {
      console.error('Error fetching archive data:', error);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users?limit=100`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data.users || res.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRGPDData(), fetchArchiveData(), fetchUsers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchRGPDData, fetchArchiveData, fetchUsers]);

  const handleExportUserData = async (userId, format = 'json') => {
    try {
      const res = await axios.get(`${API}/rgpd/export/${userId}?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Download as JSON file
      const dataStr = JSON.stringify(res.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rgpd_export_${userId}_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: 'Données exportées avec succès' });
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting user data:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de l\'export' });
    }
  };

  const handleGetExportSummary = async (userId) => {
    try {
      const res = await axios.get(`${API}/rgpd/export-summary/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExportSummary(res.data);
    } catch (error) {
      console.error('Error getting export summary:', error);
    }
  };

  const handleAnonymizeUser = async (userId, reason) => {
    try {
      await axios.post(`${API}/rgpd/anonymize/${userId}`, {
        reason,
        confirm: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', text: 'Utilisateur anonymisé avec succès' });
      setShowAnonymizeModal(false);
      fetchRGPDData();
      fetchUsers();
    } catch (error) {
      console.error('Error anonymizing user:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de l\'anonymisation' });
    }
  };

  const handleRunAutoArchive = async () => {
    try {
      const res = await axios.post(`${API}/archives/auto-archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', text: res.data.message });
      fetchArchiveData();
    } catch (error) {
      console.error('Error running auto-archive:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de l\'archivage' });
    }
  };

  const handleManualArchive = async (docType, docId) => {
    try {
      await axios.post(`${API}/archives/manual`, {
        doc_type: docType,
        doc_id: docId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', text: 'Document archivé avec succès' });
      fetchArchiveData();
    } catch (error) {
      console.error('Error archiving document:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de l\'archivage' });
    }
  };

  const handleRestoreArchive = async (archiveId) => {
    try {
      await axios.post(`${API}/archives/${archiveId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', text: 'Document restauré avec succès' });
      fetchArchiveData();
    } catch (error) {
      console.error('Error restoring archive:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la restauration' });
    }
  };

  const filteredUsers = users.filter(u => {
    if (!userSearch) return true;
    const search = userSearch.toLowerCase();
    return (
      u.email?.toLowerCase().includes(search) ||
      u.nom?.toLowerCase().includes(search) ||
      u.prenom?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <>
        {isMobile && <MobileHeader title="RGPD & Archivage" onMenuClick={onMenuClick} />}
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div>
        </div>
      </>
    );
  }

  return (
    <>
      {isMobile && <MobileHeader title="RGPD & Archivage" onMenuClick={onMenuClick} />}
      <div className={`space-y-4 md:space-y-6 ${isMobile ? 'p-4' : ''}`}>
        {/* Header */}
        {!isMobile && (
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <Shield className="mr-3 text-[#00D3A8]" size={28} />
              RGPD & Archivage
            </h1>
          </div>
        )}

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
            'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })} className="float-right">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px overflow-x-auto">
              {[
                { id: 'rgpd', label: 'Droits RGPD', icon: Shield },
                { id: 'archives', label: 'Archivage', icon: Archive },
                { id: 'consents', label: 'Consentements', icon: FileCheck },
                { id: 'registry', label: 'Registre', icon: Database }
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-[#00D3A8] text-[#00D3A8]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon size={18} className="mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* RGPD Tab */}
            {activeTab === 'rgpd' && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Utilisateurs</p>
                        <p className="text-2xl font-bold">{rgpdStats?.total_users || 0}</p>
                      </div>
                      <Users className="text-[#00D3A8]" size={32} />
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Avec consentements</p>
                        <p className="text-2xl font-bold">{rgpdStats?.users_with_consents || 0}</p>
                      </div>
                      <FileCheck className="text-green-500" size={32} />
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Anonymisés</p>
                        <p className="text-2xl font-bold">{rgpdStats?.anonymized_users || 0}</p>
                      </div>
                      <UserX className="text-orange-500" size={32} />
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Demandes en attente</p>
                        <p className="text-2xl font-bold">{rgpdStats?.pending_requests || 0}</p>
                      </div>
                      <Clock className="text-yellow-500" size={32} />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setShowExportModal(true)}
                    className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                    data-testid="export-data-btn"
                  >
                    <Download size={18} />
                    <span>Exporter données</span>
                  </button>
                  <button
                    onClick={() => setShowAnonymizeModal(true)}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                    data-testid="anonymize-btn"
                  >
                    <UserX size={18} />
                    <span>Anonymiser utilisateur</span>
                  </button>
                </div>

                {/* Retention Periods */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <Clock size={20} className="mr-2 text-[#00D3A8]" />
                    Durées de conservation (Loi française)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(retentionPeriods).map(([key, period]) => (
                      <div key={key} className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-800">{period.name}</h4>
                        <p className="text-2xl font-bold text-[#00D3A8] my-2">{period.years} ans</p>
                        <p className="text-sm text-gray-500">{period.description}</p>
                        <p className="text-xs text-gray-400 mt-2">{period.legal_basis}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Requests */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <History size={20} className="mr-2 text-[#00D3A8]" />
                    Demandes RGPD récentes
                  </h3>
                  {rgpdRequests.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Aucune demande RGPD</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisateur</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {rgpdRequests.slice(0, 10).map(req => (
                            <tr key={req.id}>
                              <td className="px-4 py-3 text-sm text-gray-800 capitalize">{req.type}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{req.user_name || req.user_email}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  req.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {req.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {new Date(req.created_at).toLocaleDateString('fr-FR')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Archives Tab */}
            {activeTab === 'archives' && (
              <div className="space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Documents archivés</p>
                        <p className="text-2xl font-bold">{archiveStats?.total_archived || 0}</p>
                      </div>
                      <Archive className="text-[#00D3A8]" size={32} />
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">En attente d'archivage</p>
                        <p className="text-2xl font-bold">{archiveStats?.pending_archive_count || 0}</p>
                      </div>
                      <Clock className="text-yellow-500" size={32} />
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Types de documents</p>
                        <p className="text-2xl font-bold">{Object.keys(archiveTypes).length}</p>
                      </div>
                      <FileText className="text-blue-500" size={32} />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleRunAutoArchive}
                    className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                    data-testid="auto-archive-btn"
                  >
                    <RefreshCw size={18} />
                    <span>Lancer l'archivage automatique</span>
                  </button>
                </div>

                {/* Pending Archives */}
                {pendingArchives.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <AlertTriangle size={20} className="mr-2 text-yellow-500" />
                      Documents à archiver ({pendingArchives.length})
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {pendingArchives.slice(0, 10).map((item, index) => (
                            <tr key={index}>
                              <td className="px-4 py-3 text-sm text-gray-800">{item.type_name}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {item.date ? new Date(item.date).toLocaleDateString('fr-FR') : '-'}
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => handleManualArchive(item.doc_type, item.doc_id)}
                                  className="text-[#00D3A8] hover:text-[#00C7A1] text-sm font-medium"
                                >
                                  Archiver
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Archived Items */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <Archive size={20} className="mr-2 text-[#00D3A8]" />
                    Documents archivés
                  </h3>
                  {archives.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Aucun document archivé</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Immeuble</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Archivé le</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conservation</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {archives.map(archive => (
                            <tr key={archive.id}>
                              <td className="px-4 py-3 text-sm text-gray-800">
                                {archiveTypes[archive.doc_type]?.name || archive.doc_type}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{archive.building_name || '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {new Date(archive.archived_at).toLocaleDateString('fr-FR')}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {archive.retention_years} ans
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => handleRestoreArchive(archive.id)}
                                  className="text-blue-500 hover:text-blue-700 text-sm font-medium mr-3"
                                  title="Restaurer"
                                >
                                  <RefreshCw size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Consents Tab */}
            {activeTab === 'consents' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FileCheck size={20} className="mr-2 text-[#00D3A8]" />
                  Types de consentements
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {consentTypes.map(consent => (
                    <div key={consent.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-gray-800 flex items-center">
                            {consent.name}
                            {consent.required && (
                              <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">
                                Obligatoire
                              </span>
                            )}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">{consent.description}</p>
                        </div>
                        {consent.required ? (
                          <Lock size={18} className="text-gray-400" />
                        ) : (
                          <FileCheck size={18} className="text-green-500" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Registry Tab */}
            {activeTab === 'registry' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <Database size={20} className="mr-2 text-[#00D3A8]" />
                  Registre des traitements
                </h3>
                <div className="space-y-4">
                  {treatments.map(treatment => (
                    <div key={treatment.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-800">{treatment.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{treatment.purpose}</p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {treatment.data_categories?.map((cat, idx) => (
                              <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {cat}
                              </span>
                            ))}
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Base légale:</span>
                              <span className="ml-2 text-gray-800">{treatment.legal_basis}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Conservation:</span>
                              <span className="ml-2 text-gray-800">{treatment.retention}</span>
                            </div>
                          </div>
                        </div>
                        {treatment.is_default && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            Par défaut
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Export Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-800">Exporter données utilisateur</h2>
                  <button onClick={() => { setShowExportModal(false); setSelectedUser(null); setExportSummary(null); }} className="text-gray-400 hover:text-gray-600">
                    <X size={24} />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher un utilisateur</label>
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Email, nom ou prénom..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  />
                </div>
                
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                  {filteredUsers.slice(0, 20).map(u => (
                    <button
                      key={u.id}
                      onClick={() => { setSelectedUser(u); handleGetExportSummary(u.id); }}
                      className={`w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-100 ${
                        selectedUser?.id === u.id ? 'bg-[#00D3A8]/10' : ''
                      }`}
                    >
                      <p className="font-medium text-gray-800">{u.prenom} {u.nom}</p>
                      <p className="text-sm text-gray-500">{u.email}</p>
                    </button>
                  ))}
                </div>
                
                {selectedUser && exportSummary && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-800 mb-2">Résumé des données</h4>
                    <p className="text-sm text-gray-600 mb-2">Total: {exportSummary.total_records} enregistrements</p>
                    <div className="space-y-1">
                      {exportSummary.data_categories?.map((cat, idx) => (
                        <p key={idx} className="text-sm text-gray-500">• {cat.name}: {cat.count}</p>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => { setShowExportModal(false); setSelectedUser(null); setExportSummary(null); }}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => handleExportUserData(selectedUser.id, 'json')}
                    disabled={!selectedUser}
                    className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white px-6 py-2 rounded-lg flex items-center space-x-2 disabled:opacity-50"
                    data-testid="confirm-export-btn"
                  >
                    <Download size={18} />
                    <span>Exporter JSON</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Anonymize Modal */}
        {showAnonymizeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-800 flex items-center">
                    <AlertTriangle className="mr-2 text-orange-500" size={24} />
                    Anonymiser utilisateur
                  </h2>
                  <button onClick={() => { setShowAnonymizeModal(false); setSelectedUser(null); }} className="text-gray-400 hover:text-gray-600">
                    <X size={24} />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-orange-800 text-sm">
                    <strong>Attention :</strong> Cette action est irréversible. Les données personnelles seront supprimées et remplacées par des valeurs anonymes.
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher un utilisateur</label>
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Email, nom ou prénom..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  />
                </div>
                
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                  {filteredUsers.filter(u => !u.is_anonymized).slice(0, 20).map(u => (
                    <button
                      key={u.id}
                      onClick={() => setSelectedUser(u)}
                      className={`w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-100 ${
                        selectedUser?.id === u.id ? 'bg-orange-50' : ''
                      }`}
                    >
                      <p className="font-medium text-gray-800">{u.prenom} {u.nom}</p>
                      <p className="text-sm text-gray-500">{u.email}</p>
                    </button>
                  ))}
                </div>
                
                {selectedUser && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">
                      Utilisateur sélectionné: <strong>{selectedUser.prenom} {selectedUser.nom}</strong> ({selectedUser.email})
                    </p>
                  </div>
                )}
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => { setShowAnonymizeModal(false); setSelectedUser(null); }}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => handleAnonymizeUser(selectedUser.id, 'Demande RGPD')}
                    disabled={!selectedUser}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg flex items-center space-x-2 disabled:opacity-50"
                    data-testid="confirm-anonymize-btn"
                  >
                    <UserX size={18} />
                    <span>Anonymiser</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RGPDArchivage;
